import React, { useEffect } from 'react';
import {
  Box,
  Button,
  ButtonProps,
  Chip,
  ChipProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  Grid,
  IconProps,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

import { useDispatch } from 'react-redux';
import { TP_COLORS } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import { getCurrencySign } from '../../Diagnostic';
import {
  getCountry,
  checkDiscountEligibility,
  isMobileScreen,
} from '../../../../helper';

import {
  getPremiumProductPrice,
  getStripeState,
} from '../../../../store/StripeSlice';
import { getAuthState } from '../../../../store/AuthSlice';

import BasicLoader from '../../../../components/Loaders/BasicLoader';

const buttonProps: ButtonProps = {
  disableElevation: true,
  fullWidth: true,
  sx: {
    maxWidth: '214px',
    fontSize: '13px',
    fontWeight: 600,
  },
  variant: 'contained',
};

const titleProps: TypographyProps = {
  variant: 'h5',
  fontSize: '22px',
  fontWeight: 600,
  marginLeft: '5px',
  lineHeight: '1.4',
};

const titleStackProps: StackProps = {
  flexDirection: isMobileScreen() ? 'column' : 'row',
  alignItems: isMobileScreen() ? 'center' : 'flex-end',
  justifyContent: 'center',
};

const highlightsStackStyles: StackProps['sx'] = {
  p: '12px',
  color: TP_COLORS.blue.dark,
};

const checkIconProps: IconProps['sx'] = {
  style: {
    color: '#01A79F',
    marginRight: '8px',
  },
};

const stackProps: StackProps = {
  flexDirection: 'row',
  alignItems: 'center',
  margin: '5px 0 3px 20px',
};

const chipProps: ChipProps = {
  size: 'small',
  sx: {
    height: '20px',
    fontSize: '12px',
    borderRadius: '4px',
    backgroundColor: '#B76E00',
    color: TP_COLORS.white,
    marginLeft: '8px',
  },
};

interface LeadGenModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (id: string) => void;
}

const planHighlights = [
  '200+ Subject Videos',
  '50000+ Practice Questions',
  'AI Assistance',
  'Progress Tracker',
  'Topic Planner',
  'Paper wise Quizzes',
  'Assessments',
];

const PremiumModal: React.FC<LeadGenModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const stripeState = useAppSelector(getStripeState);

  const { user } = authState;
  const { premiumPricesList, status } = stripeState;
  const monthlyPrice = premiumPricesList.length
    ? premiumPricesList[0]
    : undefined;

  // const quarterlyPrice = premiumPricesList.length
  //   ? premiumPricesList[1]
  //   : undefined;

  const yearlyPrice = premiumPricesList.length
    ? premiumPricesList[2]
    : undefined;

  const yearlyTotal = (monthlyPrice?.unitAmount || 1) * 12;
  const yearlyOffRate =
    Math.round(
      (((yearlyTotal - (yearlyPrice?.unitAmount || 1)) / yearlyTotal) * 100) / 5
    ) * 5;

  // const quarterlyTotal = (monthlyPrice?.unitAmount || 1) * 3;
  // const quarterlyOffRate =
  //   Math.round(
  //     (((quarterlyTotal - (quarterlyPrice?.unitAmount || 1)) / quarterlyTotal) *
  //       100) /
  //       5
  //   ) * 5;

  const currency = getCurrencySign(monthlyPrice?.currency || '');
  const country = getCountry();
  const isDiscountEligible = checkDiscountEligibility();

  useEffect(() => {
    if (user) {
      dispatch(getPremiumProductPrice({ userId: user._id, country }));
    }
  }, []);

  return (
    <Dialog
      open={open}
      hideBackdrop
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: { xs: 'unset', sm: '550px', md: '550px', lg: '550px' },
          maxWidth: { xs: 'unset', sm: '550px', md: '550px', lg: '550px' },
          borderRadius: '20px',
        },
      }}
    >
      <Typography
        fontWeight="600"
        textAlign="center"
        bgcolor="#FFAB00"
        padding="9px"
      >
        You have exceeded the limit for viewing this content
      </Typography>
      <DialogContent>
        <Stack {...titleStackProps}>
          <DialogContentText>Unlock your</DialogContentText>
          <Typography {...titleProps}>Full Learning Potential !</Typography>
        </Stack>

        <Stack alignItems="center" marginBottom="10px">
          <DialogContentText align="center" maxWidth="400px">
            Upgrade to premium for exclusive features and personalized support
          </DialogContentText>
        </Stack>
        <Stack sx={highlightsStackStyles}>
          {planHighlights.map((item, index) => (
            <Stack key={index} {...stackProps}>
              <CheckIcon {...checkIconProps} />
              <Typography fontSize="18px">{item}</Typography>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <Divider variant="middle" />
      {status === 'loading' && premiumPricesList.length === 0 ? (
        <div style={{ padding: '30px' }}>
          <BasicLoader />
        </div>
      ) : (
        <DialogActions sx={{ padding: '24px', flexDirection: 'column' }}>
          <Grid
            container
            spacing={2}
            alignItems="flex-end"
            justifyContent="center"
          >
            {/* Yearly */}
            {yearlyPrice && (
              <Grid item xs={12} sm={6}>
                <Stack alignItems="center" spacing={1}>
                  <Box display="flex" alignItems="center">
                    <Typography fontSize="14px" fontWeight="600">
                      12 months
                    </Typography>
                    <Chip {...chipProps} label={`Save${yearlyOffRate}% off`} />
                  </Box>
                  <Box display="flex" alignItems="flex-end">
                    <Typography
                      fontSize="23px"
                      fontWeight="600"
                      lineHeight="26px"
                    >
                      {currency}
                    </Typography>
                    <Typography
                      fontSize="30px"
                      fontWeight="600"
                      lineHeight="30px"
                      marginRight="3px"
                    >
                      {(yearlyPrice.unitAmount / 1200).toFixed()}
                    </Typography>
                    <Typography
                      fontSize="16px"
                      fontWeight="600"
                      color={TP_COLORS.tutopiyaGreenMain.darken}
                    >
                      /Month
                    </Typography>
                  </Box>
                  <Typography
                    fontSize="12px"
                    color={TP_COLORS.tutopiyaGreenMain.darken}
                  >
                    Billed at {currency}
                    {yearlyPrice.unitAmount / 100}
                    /year
                  </Typography>
                  <Button
                    {...buttonProps}
                    color="primary"
                    onClick={() => onConfirm(yearlyPrice.id || '')}
                  >
                    Enroll Now
                  </Button>
                </Stack>
              </Grid>
            )}

            {/* Quarterly */}
            {/* {quarterlyPrice && (
              <Grid item xs={12} sm={6}>
                <Stack alignItems="center" spacing={1}>
                  <Box display="flex" alignItems="center">
                    <Typography fontSize="14px" fontWeight="600">
                      3 months
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="flex-end">
                    <Typography
                      fontSize="23px"
                      fontWeight="600"
                      lineHeight="26px"
                    >
                      {currency}
                    </Typography>
                    <Typography
                      fontSize="30px"
                      fontWeight="600"
                      lineHeight="30px"
                      marginRight="3px"
                    >
                      {(quarterlyPrice.unitAmount / 300).toFixed()}
                    </Typography>
                    <Typography
                      fontSize="16px"
                      fontWeight="600"
                      color={TP_COLORS.tutopiyaGreenMain.darken}
                    >
                      /Month
                    </Typography>
                  </Box>
                  <Typography
                    fontSize="12px"
                    color={TP_COLORS.tutopiyaGreenMain.darken}
                  >
                    Billed at {currency}
                    {quarterlyPrice.unitAmount / 100}
                    /Quarter
                  </Typography>
                  <Button
                    {...buttonProps}
                    color="inherit"
                    onClick={() => onConfirm(quarterlyPrice.id || '')}
                  >
                    Enroll Now
                  </Button>
                </Stack>
              </Grid>
            )} */}

            {/* Monthly */}
            {monthlyPrice && (
              <Grid item xs={12} sm={6}>
                <Stack alignItems="center" spacing={1}>
                  <Box display="flex" alignItems="center">
                    <Typography fontSize="14px" fontWeight="600">
                      1 month
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="flex-end">
                    <Typography
                      fontSize="23px"
                      fontWeight="600"
                      lineHeight="26px"
                    >
                      {currency}
                    </Typography>
                    <Typography
                      fontSize="30px"
                      fontWeight="600"
                      lineHeight="30px"
                      marginRight="3px"
                    >
                      {(monthlyPrice.unitAmount / 100).toFixed()}
                    </Typography>
                    <Typography
                      fontSize="16px"
                      fontWeight="600"
                      color={TP_COLORS.tutopiyaGreenMain.darken}
                    >
                      /Month
                    </Typography>
                  </Box>
                  <Typography
                    fontSize="12px"
                    color={TP_COLORS.tutopiyaGreenMain.darken}
                  >
                    Billed at {currency}
                    {monthlyPrice.unitAmount / 100} a month
                  </Typography>
                  <Button
                    {...buttonProps}
                    color="inherit"
                    onClick={() => onConfirm(monthlyPrice.id || '')}
                  >
                    Enroll Now
                  </Button>
                </Stack>
              </Grid>
            )}

            {isDiscountEligible && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '16px 13px',
                    margin: { xs: '0', sm: '16px' },
                    borderRadius: '7px',
                    background: '#BEA4F9',
                    border: '1px solid #1E293B',
                  }}
                >
                  <Typography fontSize="14px" fontWeight="600">
                    🎉 15% Off&nbsp;
                  </Typography>
                  <Typography fontSize="14px">
                    Premium! Use code Disc15 on the payment page.
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PremiumModal;
